import Image from "next/image";
import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";
import { ArticleGalleryCarousel } from "@/components/site/article-gallery-carousel";
import { GettyExternalEmbed } from "@/components/site/getty-embed";
import { TwitterEmbed } from "@/components/site/twitter-embed";
import type { LexicalNode } from "@/lib/article-content-blocks";
import { socialEmbedAspectRatio, socialEmbedIframeSrc, socialEmbedTitle } from "@/lib/social-embeds";
import { parseYouTubeVideoUrl, youtubeEmbedUrl } from "@/lib/youtube-video";
import { externalEmbedAspectRatioCss, externalEmbedProviderLabel, parseExternalEmbed } from "@/lib/external-embed";
import {
  ARTICLE_MEDIA_DEFAULT_WIDTH,
  articleEmbedInnerClassName,
  articleEmbedInnerLayoutStyle,
  articleEmbedOuterClassName,
  articleEmbedOuterLayoutStyle,
  articleImageClassName,
  articleImageEffectiveLayout,
  articleImageLayoutStyle,
  articleMediaWidthFromLegacySize,
  clampArticleMediaWidth,
} from "@/lib/article-media-positioning";

function isExternalHref(href: string) {
  return href.startsWith("http://") || href.startsWith("https://") || href.startsWith("mailto:");
}

const EDITORIAL_TEXT_STYLE_CLASS: Record<string, string> = {
  lead: "text-lg leading-8 text-foreground",
  highlight: "rounded-[3px] bg-yellow-100/40 font-semibold",
  subtle: "text-sm leading-6 text-muted-foreground",
  caption: "text-xs italic leading-5 text-muted-foreground",
};
const TABLE_HEADER_TEXT_HIGHLIGHT_CLASS = "inline bg-[#A4243B] px-[0.18em] py-[0.04em] font-semibold leading-[1.6] text-white [box-decoration-break:clone] [-webkit-box-decoration-break:clone]";

function editorialTextStyleClass(style: unknown) {
  if (typeof style !== "string") return "";
  const value = style.match(/--laszti-editorial-style\s*:\s*([a-z-]+)/)?.[1] ?? "";
  return EDITORIAL_TEXT_STYLE_CLASS[value] ?? "";
}

function textNodeStyle(style: unknown) {
  if (typeof style !== "string") return undefined;
  const fontSize = style.match(/font-size\s*:\s*((?:[1-6]\d|7[0-2])px)/)?.[1];
  return fontSize ? ({ fontSize } as CSSProperties) : undefined;
}

function textAlignClass(format: unknown) {
  if (format === "left" || format === "start") return "text-left";
  if (format === "center") return "text-center";
  if (format === "right" || format === "end") return "text-right";
  if (format === "justify") return "text-justify";
  return "";
}

function safeEmbedWidth(width: unknown, legacySize?: unknown) {
  return clampArticleMediaWidth(width, articleMediaWidthFromLegacySize(legacySize));
}

function publicEmbedLayoutDebug(kind: string, payload: Record<string, unknown>) {
  if (process.env.NODE_ENV === "development") console.log(`[ArticleRenderer ${kind} embed layout]`, payload);
}

function renderText(node: LexicalNode, key: React.Key) {
  let children: ReactNode = node.text ?? "";
  const format = typeof node.format === "number" ? node.format : 0;
  if (format & 1) children = <strong>{children}</strong>;
  if (format & 2) children = <em>{children}</em>;
  if (format & 4) children = <s>{children}</s>;
  if (format & 8) children = <span className="underline underline-offset-2">{children}</span>;
  if (format & 16) children = <code>{children}</code>;
  return (
    <span key={key} className={editorialTextStyleClass(node.style) || undefined} style={textNodeStyle(node.style)}>
      {children}
    </span>
  );
}

function renderInline(nodes?: LexicalNode[]) {
  return nodes?.map((node, index) => renderNode(node, index)) ?? null;
}

function renderTableCellChildren(nodes?: LexicalNode[], isHeader = false) {
  return nodes?.map((node, index) => {
    if (node.type === "paragraph") {
      return (
        <p key={index} className={[isHeader ? `m-0 ${TABLE_HEADER_TEXT_HIGHLIGHT_CLASS}` : "m-0", textAlignClass(node.format)].filter(Boolean).join(" ")}>
          {renderInline(node.children)}
        </p>
      );
    }
    return renderNode(node, index);
  }) ?? null;
}

function tableColumnWidths(node: LexicalNode) {
  return Array.isArray(node.colWidths) ? node.colWidths.filter((width): width is number => typeof width === "number" && Number.isFinite(width) && width > 0) : [];
}

function tableWidthPercent(node: LexicalNode) {
  if (typeof node.tableWidth === "number" && Number.isFinite(node.tableWidth)) return Math.max(50, Math.min(100, Math.round(node.tableWidth)));
  if (typeof node.textStyle !== "string") return null;
  const width = Number(node.textStyle.match(/--laszti-table-width\s*:\s*(\d+(?:\.\d+)?)/)?.[1]);
  return Number.isFinite(width) ? Math.max(50, Math.min(100, Math.round(width))) : null;
}

function articleImageClass(attrs: LexicalNode) {
  const layout = attrs.layout === "wide" || attrs.layout === "left" || attrs.layout === "right" || attrs.layout === "center" ? attrs.layout : "full";
  const wrap = attrs.wrap === "float-left" || attrs.wrap === "float-right" || attrs.wrap === "shape-left" || attrs.wrap === "shape-right" ? attrs.wrap : "";
  const size = attrs.size === "sm" || attrs.size === "md" || attrs.size === "lg" || attrs.size === "full" ? attrs.size : "full";
  return articleImageClassName(layout, size, wrap);
}

function articleImageLayout(attrs: LexicalNode) {
  return articleImageEffectiveLayout(attrs.layout, attrs.wrap);
}

function imageObjectPosition(attrs: LexicalNode) {
  const focus = attrs.focusPoint;
  if (!focus || typeof focus.x !== "number" || typeof focus.y !== "number") return "50% 50%";
  const x = Math.max(0, Math.min(100, Math.round(focus.x)));
  const y = Math.max(0, Math.min(100, Math.round(focus.y)));
  return `${x}% ${y}%`;
}

function imageAspectRatioStyle(attrs: LexicalNode) {
  const width = typeof attrs.originalWidth === "number" && Number.isFinite(attrs.originalWidth) && attrs.originalWidth > 0 ? Math.round(attrs.originalWidth) : null;
  const height = typeof attrs.originalHeight === "number" && Number.isFinite(attrs.originalHeight) && attrs.originalHeight > 0 ? Math.round(attrs.originalHeight) : null;
  return { aspectRatio: width && height ? `${width} / ${height}` : "16 / 9" } as CSSProperties;
}

function externalEmbedAspectRatioStyle(attrs: LexicalNode, parsed: NonNullable<ReturnType<typeof parseExternalEmbed>>) {
  if (
    parsed.provider === "getty" &&
    typeof attrs.embedWidth === "number" &&
    Number.isFinite(attrs.embedWidth) &&
    attrs.embedWidth > 0 &&
    typeof attrs.embedHeight === "number" &&
    Number.isFinite(attrs.embedHeight) &&
    attrs.embedHeight > 0
  ) {
    return `${attrs.embedWidth} / ${attrs.embedHeight}`;
  }
  return externalEmbedAspectRatioCss(parsed);
}

function articleImageStyle(attrs: LexicalNode) {
  return articleImageLayoutStyle(attrs) as CSSProperties;
}

function bestImageUrl(node: LexicalNode) {
  if (node.variants?.["1600x900"]) return node.variants["1600x900"];
  return node.src ?? "";
}

function renderNode(node: LexicalNode, key: React.Key): ReactNode {
  if (node.type === "text") return renderText(node, key);
  if (node.type === "paragraph") {
    return <p key={key} className={["max-w-3xl", textAlignClass(node.format)].filter(Boolean).join(" ")}>{renderInline(node.children)}</p>;
  }
  if (node.type === "heading") {
    if (node.tag === "h3") {
      return <h3 key={key} className={["max-w-3xl", textAlignClass(node.format)].filter(Boolean).join(" ")}>{renderInline(node.children)}</h3>;
    }
    return <h2 key={key} className={["max-w-3xl", textAlignClass(node.format)].filter(Boolean).join(" ")}>{renderInline(node.children)}</h2>;
  }
  if (node.type === "list") {
    const children = node.children?.map((child, index) => renderNode(child, index));
    if (node.listType === "number") return <ol key={key} className={["max-w-3xl list-decimal", textAlignClass(node.format)].filter(Boolean).join(" ")}>{children}</ol>;
    return <ul key={key} className={["max-w-3xl list-disc", textAlignClass(node.format)].filter(Boolean).join(" ")}>{children}</ul>;
  }
  if (node.type === "listitem") {
    return <li key={key}>{node.children?.map((child, index) => child.type === "paragraph" ? renderInline(child.children) : renderNode(child, index))}</li>;
  }
  if (node.type === "table") {
    const colWidths = tableColumnWidths(node);
    const widthPercent = tableWidthPercent(node);
    const tableStyle = {
      width: widthPercent ? `${widthPercent}%` : "100%",
      maxWidth: "100%",
      minWidth: colWidths.length ? Math.max(576, colWidths.reduce((sum, width) => sum + width, 0)) : 576,
    } as CSSProperties;
    return (
      <div key={key} className="my-8 max-w-full overflow-x-auto pb-1">
        <table className="border-collapse text-left text-sm leading-6 text-zinc-900" style={tableStyle}>
          {colWidths.length ? (
            <colgroup>
              {colWidths.map((width, index) => (
                <col key={index} style={{ width }} />
              ))}
            </colgroup>
          ) : null}
          <tbody>{node.children?.map((child, index) => renderNode(child, index))}</tbody>
        </table>
      </div>
    );
  }
  if (node.type === "tablerow") {
    return <tr key={key}>{node.children?.map((child, index) => renderNode(child, index))}</tr>;
  }
  if (node.type === "tablecell") {
    const isHeader = Boolean(node.headerState);
    const Cell = isHeader ? "th" : "td";
    return (
      <Cell
        key={key}
        colSpan={typeof node.colSpan === "number" ? node.colSpan : undefined}
        rowSpan={typeof node.rowSpan === "number" ? node.rowSpan : undefined}
        className={[
          "border border-zinc-300 px-3 py-2 [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2",
          textAlignClass(node.format) || "text-left",
          isHeader ? "bg-[#2C3E46] align-middle font-semibold text-white [&_*]:text-white [&_p]:font-semibold [&_a]:text-white" : "bg-white align-top text-zinc-900 [&_a]:text-[#A4243B]",
        ].join(" ")}
        style={typeof node.width === "number" && Number.isFinite(node.width) ? { width: node.width } : undefined}
      >
        {renderTableCellChildren(node.children, isHeader)}
      </Cell>
    );
  }
  if (node.type === "quote" || node.type === "pull-quote") {
    return (
      <blockquote key={key} className="max-w-3xl">
        {renderInline(node.children)}
      </blockquote>
    );
  }
  if (node.type === "link" && typeof node.url === "string") {
    return isExternalHref(node.url) ? (
      <a key={key} href={node.url} rel="noopener noreferrer">
        {renderInline(node.children)}
      </a>
    ) : (
      <Link key={key} href={node.url}>
        {renderInline(node.children)}
      </Link>
    );
  }
  if (node.type === "article-image" && typeof node.src === "string") {
    return (
      <figure key={key} className={articleImageClass(node)} data-layout={process.env.NODE_ENV === "development" ? articleImageLayout(node) : undefined} style={articleImageStyle(node)}>
        <div className="article-media-frame relative overflow-hidden rounded-lg bg-zinc-100" style={imageAspectRatioStyle(node)}>
          <Image
            src={bestImageUrl(node)}
            alt={typeof node.alt === "string" ? node.alt : ""}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 92vw, 920px"
            className="object-cover"
            style={{ objectPosition: imageObjectPosition(node) }}
          />
        </div>
        {node.caption || node.credit ? (
          <figcaption className="mt-2 text-xs italic leading-5 text-zinc-500">
            {[node.caption, node.credit].filter(Boolean).join(" / ")}
          </figcaption>
        ) : null}
      </figure>
    );
  }
  if (node.type === "divider") return <hr key={key} className="my-9 max-w-3xl border-0 border-t border-zinc-300" />;
  if (node.type === "youtube-video" && typeof node.videoId === "string") {
    const outerClassName = articleEmbedOuterClassName(node.layout);
    const innerClassName = articleEmbedInnerClassName("overflow-hidden rounded-lg border border-zinc-200 bg-white");
    publicEmbedLayoutDebug("youtube", {
      layout: node.layout,
      width: node.width,
      size: node.size,
      offsetX: node.offsetX,
      offsetY: node.offsetY,
      outerClassName,
      innerClassName,
    });
    return (
      <figure key={key} className={outerClassName} style={articleEmbedOuterLayoutStyle(node) as CSSProperties}>
        <div className={innerClassName} style={articleEmbedInnerLayoutStyle(node) as CSSProperties}>
          <div className="article-iframe-wrapper aspect-video bg-zinc-950">
            <iframe
              src={parseYouTubeVideoUrl(node.url ?? "")?.embedUrl ?? youtubeEmbedUrl(node.videoId)}
              title="YouTube videó"
              className="h-full w-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
          {node.source ? <figcaption className="px-3 py-2 text-xs italic leading-5 text-zinc-500">{node.source}</figcaption> : null}
        </div>
      </figure>
    );
  }
  if (node.type === "social-embed" && typeof node.embedUrl === "string") {
    const platform = node.platform === "facebook" || node.platform === "instagram" || node.platform === "tiktok" || node.platform === "twitter" || node.platform === "x" ? node.platform : "facebook";
    const kind = node.kind === "video" || node.kind === "reel" ? node.kind : "post";
    const width = safeEmbedWidth(node.width, node.size) ?? ARTICLE_MEDIA_DEFAULT_WIDTH;
    const iframeSrc = socialEmbedIframeSrc(node.embedUrl, width);
    const aspectRatio = socialEmbedAspectRatio({ platform, kind, embedUrl: iframeSrc });
    const isTwitterEmbed = platform === "x" || platform === "twitter";
    const outerClassName = articleEmbedOuterClassName(node.layout, "social-embed");
    const innerClassName = articleEmbedInnerClassName("overflow-hidden rounded-lg border border-zinc-200 bg-white");
    publicEmbedLayoutDebug("social", {
      platform,
      kind,
      layout: node.layout,
      width: node.width,
      computedWidth: width,
      size: node.size,
      offsetX: node.offsetX,
      offsetY: node.offsetY,
      outerClassName,
      innerClassName,
    });
    return (
      <figure key={key} className={outerClassName} style={articleEmbedOuterLayoutStyle(node) as CSSProperties}>
        <div className={innerClassName} style={articleEmbedInnerLayoutStyle(node) as CSSProperties}>
          <div className="article-iframe-wrapper bg-[#FCFBF8]" style={isTwitterEmbed ? undefined : { aspectRatio }}>
            {isTwitterEmbed && typeof node.url === "string" ? (
              <TwitterEmbed url={node.url} fallbackText={node.fallbackText} className="px-3 py-3" />
            ) : (
              <iframe
                src={iframeSrc}
                title={socialEmbedTitle({ platform, kind })}
                className="block h-full w-full"
                loading="lazy"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
              />
            )}
          </div>
        </div>
      </figure>
    );
  }
  if (node.type === "external-embed" && typeof node.embedUrl === "string") {
    const parsed = parseExternalEmbed(node.embedUrl);
    if (!parsed || parsed.provider === "youtube" || parsed.provider === "social") return null;
    const gettyItemId = parsed.provider === "getty" ? parsed.itemId ?? node.itemId : null;
    const outerClassName = articleEmbedOuterClassName(node.layout, "external-embed");
    const innerClassName = articleEmbedInnerClassName("overflow-hidden rounded-lg border border-zinc-200 bg-white");
    return (
      <figure key={key} className={outerClassName} style={articleEmbedOuterLayoutStyle(node) as CSSProperties}>
        <div className={innerClassName} style={articleEmbedInnerLayoutStyle(node) as CSSProperties}>
          <div className="article-iframe-wrapper bg-[#FCFBF8]" style={{ aspectRatio: externalEmbedAspectRatioStyle(node, parsed) }}>
            {parsed.provider === "getty" && gettyItemId ? (
              <GettyExternalEmbed embed={parsed} anchorId={parsed.anchorId ?? node.anchorId} sig={parsed.sig ?? node.sig} itemId={gettyItemId} width={parsed.width ?? node.embedWidth} height={parsed.height ?? node.embedHeight} caption={typeof parsed.caption === "boolean" ? parsed.caption : node.showCaption} tld={parsed.tld ?? node.tld} is360={typeof parsed.is360 === "boolean" ? parsed.is360 : node.is360} />
            ) : parsed.provider === "getty" ? null : (
              <iframe
                src={parsed.embedUrl}
                title={node.caption || externalEmbedProviderLabel(parsed)}
                className="block h-full w-full"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            )}
          </div>
          {node.caption || node.source ? <figcaption className="px-3 py-2 text-xs italic leading-5 text-zinc-500">{[node.caption, node.source].filter(Boolean).join(" / ")}</figcaption> : null}
        </div>
      </figure>
    );
  }
  if (node.type === "gallery" && Array.isArray(node.items)) {
    return <ArticleGalleryCarousel key={key} items={node.items} layout={node.layout} aspectRatio={node.aspectRatio} objectFit={node.objectFit} width={node.width} />;
  }
  if (node.type === "linebreak") return <br key={key} />;
  return null;
}

export function LexicalArticleRenderer({ editorState }: { editorState: LexicalNode }) {
  return (
    <div className="article-content clear-both max-w-none break-words [overflow-wrap:anywhere]">
      {editorState.root?.children?.map((node, index) => renderNode(node, index))}
    </div>
  );
}
